import { get, panFor, play, set, unlock } from '@foleyjs/core';
import type { CueName, PlayOptions } from '@foleyjs/core';

/**
 * Engine settings for Foley (https://usefoley.dev).
 *
 * `mechanical` suits the 8-bit/retro personality in `docs/STYLE-GUIDE.md`, and a
 * modest `localize` pans each cue toward the element that fired it, so cards
 * around the table sound like they came from their seat.
 */
export const FOLEY_SETTINGS: Parameters<typeof set>[0] = {
  volume: 0.55,
  space: 0.18,
  localize: 0.35,
  theme: 'mechanical',
};

let muted = false;

/**
 * Mute or unmute every cue.
 *
 * Foley's own `muted` setting only zeroes the master gain — it still builds an
 * AudioContext and schedules a voice for each cue, which on mobile can duck
 * whatever the player is already listening to. Dropping cues here means a muted
 * tab never touches Web Audio at all. The engine flag is set too, so anything
 * that reaches `play()` by another route is still silent.
 */
export const setMuted = (value: boolean): void => {
  muted = value;
  set({ muted: value });
};

/**
 * Play a cue without ever letting audio break the game.
 *
 * Most of our cues fire from React effects, where an exception (a browser with
 * no Web Audio, an AudioContext the browser refuses to create) would escape to
 * the error boundary and blank the page. Sound is decoration; failure is mute.
 */
export const playCue = (name: CueName, opts?: PlayOptions): void => {
  if (muted) {
    return;
  }

  try {
    play(name, opts);
  } catch (error) {
    console.warn('[foley] Unable to play cue.', name, error);
  }
};

interface CueBinding {
  attribute: string;
  event: 'click' | 'pointerdown' | 'pointerup' | 'pointerover';
  fallback: CueName;
}

const CUE_BINDINGS: readonly CueBinding[] = [
  { attribute: 'data-foley-click', event: 'click', fallback: 'tap' },
  { attribute: 'data-foley-press', event: 'pointerdown', fallback: 'press' },
  { attribute: 'data-foley-release', event: 'pointerup', fallback: 'release' },
  { attribute: 'data-foley-hover', event: 'pointerover', fallback: 'tick' },
];

/** Companion to `data-foley-hover`: semitones to transpose that cue by. */
const HOVER_PITCH_ATTRIBUTE = 'data-foley-hover-pitch';

const hoverShouldPlay = (element: Element, event: PointerEvent): boolean => {
  // A touch dispatches pointerover immediately before the click. Without this a
  // tap on a vote card makes two sounds where a mouse click makes one.
  if (event.pointerType !== 'mouse') {
    return false;
  }

  // pointerover fires again when the pointer crosses into a child element.
  const from = event.relatedTarget;
  if (from instanceof Node && element.contains(from)) {
    return false;
  }

  return get().hover;
};

/**
 * Wire the `data-foley-*` attributes by delegating from the document.
 *
 * Foley ships `bind()`, but it attaches listeners only to the elements that
 * exist when it runs — in this SPA nearly everything (modals, vote cards, the
 * conditional round buttons) mounts later. Re-calling it is not an option
 * either: every call appends a fresh pair of document-level unlock listeners
 * with no dedupe guard, so a tab that re-binds on each render accumulates them
 * until the next gesture. Delegation is one fixed set of listeners that covers
 * every element the app will ever render.
 *
 * Returns a teardown function.
 */
export const bindCueDelegation = (): (() => void) => {
  const bound = CUE_BINDINGS.map(({ attribute, event, fallback }) => {
    const handler = (rawEvent: Event) => {
      const target = rawEvent.target;
      if (!(target instanceof Element)) {
        return;
      }

      const element = target.closest(`[${attribute}]`);
      if (!(element instanceof HTMLElement) || element.matches(':disabled')) {
        return;
      }

      const options: PlayOptions = { pan: panFor(element) };

      if (event === 'pointerover') {
        const pointerEvent = rawEvent as PointerEvent;
        if (!hoverShouldPlay(element, pointerEvent)) {
          return;
        }

        const rawPitch = element.getAttribute(HOVER_PITCH_ATTRIBUTE);
        if (rawPitch !== null && Number.isFinite(Number(rawPitch))) {
          options.pitch = Number(rawPitch);
        }
      }

      playCue((element.getAttribute(attribute) || fallback) as CueName, options);
    };

    document.addEventListener(event, handler, true);
    return { event, handler };
  });

  return () => {
    bound.forEach(({ event, handler }) => document.removeEventListener(event, handler, true));
  };
};

/**
 * Start the audio engine on the first user gesture.
 *
 * `play()` unlocks on its own, but game events fire from effects — without this
 * the cue for a teammate joining before you have clicked anything is lost to a
 * suspended AudioContext. Returns a teardown function.
 */
export const primeAudioOnFirstGesture = (): (() => void) => {
  const events = ['pointerdown', 'keydown'] as const;

  const stop = () => {
    events.forEach((event) => document.removeEventListener(event, handler, true));
  };

  const handler = () => {
    stop();
    if (muted) {
      return;
    }

    try {
      unlock();
    } catch (error) {
      console.warn('[foley] Unable to start audio.', error);
    }
  };

  events.forEach((event) => document.addEventListener(event, handler, true));
  return stop;
};

const SOUND_STORAGE_KEY = 'panda-poker-sound-enabled';

/** Sound is on unless the player has explicitly turned it off. */
export const getStoredSoundEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(SOUND_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
};

export const storeSoundEnabled = (enabled: boolean): void => {
  try {
    window.localStorage.setItem(SOUND_STORAGE_KEY, String(enabled));
  } catch (error) {
    console.warn('[foley] Unable to save sound preference to localStorage.', error);
  }
};
